import { ToastContainer } from './Toast';
import React, { useState, useEffect } from 'react';
import SignedProposalModal from './SignedProposalModal';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const canSwitchToMobile = ['admin', 'super_admin', 'supervisor', 'manager'].includes(user?.role);

  const handleSwitchToMobile = () => {
    const token = localStorage.getItem('token');
    // Open mobile in new tab, pass only token (mobile validates with API)
    window.location.href = 'https://tech.safecorepest.com?tk=' + token;
  };

  const [pendingSignedCount, setPendingSignedCount] = useState(0);
  const [pendingReportsCount, setPendingReportsCount] = useState(0);
  const [overdueInvoiceCount, setOverdueInvoiceCount] = useState(0);
  const [companyLicenses, setCompanyLicenses] = useState([]);
  const [licenseModal, setLicenseModal] = useState(null); // { licenses: [...] }

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const token = localStorage.getItem('token') || '';
        if (!token) return;
        // Fetch pending reports count
        try {
          const rptRes = await fetch('https://api.safecorepest.com/api/report-queue/count', {
            headers: { 'Authorization': 'Bearer ' + token }
          });
          const rptData = await rptRes.json();
          if (rptData.success) setPendingReportsCount(rptData.count || 0);
        } catch(e) {}

        const res = await fetch('https://api.safecorepest.com/api/service-agreements/pending-signed', {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        const data = await res.json();
        setPendingSignedCount((data || []).length);
      } catch (e) { /* silent */ }
    };

    fetchPendingCount();

    // SSE — update badge instantly when client signs
    const token = localStorage.getItem('token') || '';
    let evtSource = null;
    if (token) {
      try {
        evtSource = new EventSource(
          'https://api.safecorepest.com/api/events?token=' + encodeURIComponent(token)
        );
        evtSource.addEventListener('proposal_signed', () => {
          setPendingSignedCount(c => c + 1);
        });
      } catch (e) { /* SSE not supported */ }
    }

    // Listen for overdue invoices SSE event
    if (evtSource) {
      evtSource.addEventListener('invoices_overdue', (e) => {
        try {
          const data = JSON.parse(e.data);
          setOverdueInvoiceCount(c => c + (data.count || 1));
        } catch (_) { setOverdueInvoiceCount(c => c + 1); }
      });
    }

    // Fetch overdue count on mount (login check)
    const fetchOverdueCount = async () => {
      try {
        const token = localStorage.getItem('token') || '';
        if (!token) return;
        const res = await fetch('https://api.safecorepest.com/api/invoices/overdue-count', {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        const data = await res.json();
        setOverdueInvoiceCount(data.count || 0);
      } catch (e) { /* silent */ }
    };
    fetchOverdueCount();

    // Company license modal — check on every login (once per calendar day)
    const fetchCompanyLicenses = async () => {
      try {
        const tk = localStorage.getItem('token') || '';
        if (!tk) return;
        const res = await fetch('https://api.safecorepest.com/api/companies/licenses', {
          headers: { 'Authorization': 'Bearer ' + tk }
        });
        const data = await res.json();
        const lics = data.data || [];

        // Show on every login — use sessionStorage so it resets on logout/login
        const sessionKey = 'sc_lic_modal_shown';
        const alreadyShown = sessionStorage.getItem(sessionKey);
        if (alreadyShown) return;

        const urgent = lics.filter(lic => {
          if (!lic.expiry_date) return false;
          const days = Math.floor((new Date(lic.expiry_date + 'T00:00:00') - new Date()) / (1000*60*60*24));
          return days <= 7;
        });
        if (urgent.length > 0) {
          setLicenseModal({ licenses: urgent });
          sessionStorage.setItem(sessionKey, '1');
        }
      } catch (e) { console.error('License modal error:', e); }
    };
    fetchCompanyLicenses();

    return () => { if (evtSource) evtSource.close(); };
  }, []);

  const menuSections = [
    {
      title: 'OPERATIONS',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: '🏠', path: '/dashboard' },
        { id: 'live-tracking', label: 'Live Tracking', icon: '📍', path: '/live-tracking' },
        { id: 'clients', label: 'Clients', icon: '🏢', path: '/clients' },
        { id: 'schedule', label: 'Schedule', icon: '📅', path: '/schedule' },
        { id: 'operational-reports', label: 'Operational Reports', icon: '📋', path: '/operational-reports' },
        { id: 'report-queue', label: 'Report Queue', icon: '📤', path: '/report-queue', badge: pendingReportsCount },
      ]
    },
    {
      title: 'ANALYTICS',
      items: [
        { id: 'reports', label: 'Business Analytics', icon: '📊', path: '/reports' },
        { id: 'timing', label: 'Timing & Performance', icon: '⏱️', path: '/timing' },
      ]
    },
    {
      title: 'MANAGEMENT',
      items: [
        { id: 'technicians', label: 'Technicians', icon: '👷', path: '/technicians' },
        { id: 'products', label: 'Products', icon: '🧪', path: '/products' },
      ]
    },
    {
      title: 'ADMINISTRATION',
      items: [
        { id: 'proposals', label: 'Proposals', icon: '📋', path: '/proposals' },
        { id: 'invoicing', label: 'Invoicing', icon: '💵', path: '/invoicing' },
        { id: 'audit-trail', label: 'Audit Trail', icon: '🔍', path: '/audit-trail' },
        { id: 'security', label: 'Security Tracking', icon: '🔐', path: '/security' },
      ]
    },
    {
      title: 'SYSTEM',
      items: [
        { id: 'settings', label: 'Company Set Up', icon: '⚙️', path: '/settings' },
        { id: 'system-maintenance', label: 'Maintenance Tables', icon: '🗄️', path: '/system-maintenance' },
      ]
    }
  ];

  // Company license color thresholds
  const getLicenseBannerStyle = (expiryDate) => {
    if (!expiryDate) return null;
    const days = Math.floor((new Date(expiryDate + 'T00:00:00') - new Date()) / (1000*60*60*24));
    if (days < 0)   return { bg: '#7F1D1D', color: '#FFF', label: 'EXPIRED', days };
    if (days <= 7)  return { bg: '#DC2626', color: '#FFF', label: `${days}d left`, days };
    if (days <= 30) return { bg: '#FEE2E2', color: '#DC2626', label: `${days}d left`, days };
    if (days <= 60) return { bg: '#FEF3C7', color: '#D97706', label: `${days}d left`, days };
    if (days <= 90) return { bg: '#DBEAFE', color: '#1D4ED8', label: `${days}d left`, days };
    return { bg: '#E8F5E9', color: '#2E7D32', label: `${days}d left`, days };
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <button 
              style={styles.hamburger}
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              ☰
            </button>
            <h1 style={styles.logo}>
              <span style={styles.logoSafe}>Safe</span>
              <span style={styles.logoCore}>Core</span>
            </h1>
            <span style={styles.headerBadge}>Admin Panel</span>
          </div>
          <div style={styles.userSection}>
            <div style={styles.userInfo}>
              <span style={styles.userName}>{user?.first_name} {user?.last_name}</span>
              <span style={styles.userRole}>{user?.job_title || user?.role?.replace('_', ' ').toUpperCase()}</span>
            </div>
            <button onClick={handleLogout} style={styles.logoutBtn}>Logout</button>
          </div>
        </div>
      </header>

      <div style={styles.mainLayout}>
        {/* Sidebar */}
        <aside style={{...styles.sidebar, width: sidebarCollapsed ? '60px' : '240px'}}>
          {/* Mobile Switch */}
          {canSwitchToMobile && (
            <div style={styles.mobileSwitch}>
              <button onClick={handleSwitchToMobile} style={styles.mobileSwitchBtn}>
                <span>📱</span>
                {!sidebarCollapsed && <span>Field View</span>}
              </button>
            </div>
          )}

          {/* Menu Sections */}
          {menuSections.map((section) => (
            <div key={section.title} style={styles.menuSection}>
              {!sidebarCollapsed && (
                <div style={styles.sectionTitle}>{section.title}</div>
              )}
              {section.items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    ...styles.menuItem,
                    ...(isActive(item.path) ? styles.menuItemActive : {}),
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    padding: sidebarCollapsed ? '12px 0' : '10px 20px',
                    position: 'relative',
                  }}
                  onClick={() => navigate(item.path)}
                  title={item.label}
                >
                  <span style={styles.menuIcon}>{item.icon}</span>
                  {!sidebarCollapsed && <span style={styles.menuLabel}>{item.label}</span>}
                  {!sidebarCollapsed && item.id === 'report-queue' && pendingReportsCount > 0 && (
                    <span style={{ marginLeft: 'auto', background: '#DC2626', color: 'white',
                      borderRadius: '10px', fontSize: '11px', fontWeight: '700',
                      padding: '2px 7px', minWidth: '20px', textAlign: 'center' }}>
                      {pendingReportsCount}
                    </span>
                  )}
                  {!sidebarCollapsed && item.id === 'proposals' && pendingSignedCount > 0 && (
                    <span
                      onClick={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('sc:open_signed_modal')); }}
                      style={{ marginLeft:'auto', background:'#DC2626', color:'white', borderRadius:'50%', minWidth:'20px', height:'20px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:700, padding:'0 5px', cursor:'pointer' }}>
                      {pendingSignedCount}
                    </span>
                  )}
                  {sidebarCollapsed && item.id === 'proposals' && pendingSignedCount > 0 && (
                    <span
                      onClick={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('sc:open_signed_modal')); }}
                      style={{ position:'absolute', top:'6px', right:'6px', background:'#DC2626', color:'white', borderRadius:'50%', minWidth:'16px', height:'16px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'9px', fontWeight:700, cursor:'pointer' }}>
                      {pendingSignedCount}
                    </span>
                  )}
                  {!sidebarCollapsed && item.id === 'invoicing' && overdueInvoiceCount > 0 && (
                    <span
                      onClick={e => { e.stopPropagation(); navigate('/invoicing?tab=overdue'); setOverdueInvoiceCount(0); }}
                      style={{ marginLeft:'auto', background:'#DC2626', color:'white', borderRadius:'50%', minWidth:'20px', height:'20px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:700, padding:'0 5px', cursor:'pointer' }}>
                      {overdueInvoiceCount}
                    </span>
                  )}
                  {sidebarCollapsed && item.id === 'invoicing' && overdueInvoiceCount > 0 && (
                    <span
                      onClick={e => { e.stopPropagation(); navigate('/invoicing?tab=overdue'); setOverdueInvoiceCount(0); }}
                      style={{ position:'absolute', top:'6px', right:'6px', background:'#DC2626', color:'white', borderRadius:'50%', minWidth:'16px', height:'16px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'9px', fontWeight:700, cursor:'pointer' }}>
                      {overdueInvoiceCount}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}

          {/* Company info at bottom */}
          {!sidebarCollapsed && (
            <div style={styles.companyInfo}>
              <div style={styles.companyName}>Victory Pest Solutions</div>
              <div style={styles.companyPlan}>SafeCore Pro</div>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main style={styles.content}>
          {children}
        </main>
      </div>
    <SignedProposalModal />
    <ToastContainer />

    {/* ── Company License Urgent Modal (≤7 days or expired) ── */}
    {licenseModal && (
      <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ backgroundColor:'#FFF', borderRadius:'16px', padding:'32px', maxWidth:'480px', width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
          <div style={{ textAlign:'center', marginBottom:'20px' }}>
            <div style={{ fontSize:'48px', marginBottom:'8px' }}>🚨</div>
            <h2 style={{ color:'#DC2626', margin:'0 0 4px', fontSize:'22px', fontWeight:'700' }}>Company License Alert</h2>
            <p style={{ color:'#666', margin:0, fontSize:'14px' }}>Immediate action required</p>
          </div>
          {licenseModal.licenses.map((lic, i) => {
            const days = Math.floor((new Date(lic.expiry_date + 'T00:00:00') - new Date()) / (1000*60*60*24));
            const expired = days < 0;
            return (
              <div key={i} style={{ backgroundColor: expired ? '#7F1D1D' : '#FEE2E2', borderRadius:'10px', padding:'16px', marginBottom:'12px' }}>
                <div style={{ fontWeight:'700', color: expired ? '#FFF' : '#DC2626', fontSize:'16px', marginBottom:'4px' }}>
                  {expired ? '🔴 EXPIRED' : `🔴 ${days} day${days !== 1 ? 's' : ''} remaining`}
                </div>
                <div style={{ color: expired ? '#FCA5A5' : '#7F1D1D', fontSize:'13px' }}>
                  <strong>{lic.state}</strong> — {lic.license_type} #{lic.license_number}
                </div>
                {lic.expiry_date && (
                  <div style={{ color: expired ? '#FCA5A5' : '#991B1B', fontSize:'12px', marginTop:'4px' }}>
                    {expired ? 'Expired on' : 'Expires'}: {new Date(lic.expiry_date + 'T00:00:00').toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' })}
                  </div>
                )}
              </div>
            );
          })}
          <p style={{ color:'#555', fontSize:'13px', textAlign:'center', margin:'16px 0' }}>
            Please renew this license immediately and update the expiry date in Settings.
          </p>
          <div style={{ display:'flex', gap:'10px' }}>
            <button onClick={() => { setLicenseModal(null); window.location.href='/settings'; }}
              style={{ flex:1, backgroundColor:'#DC2626', color:'#FFF', border:'none', padding:'12px', borderRadius:'8px', cursor:'pointer', fontWeight:'700', fontSize:'14px' }}>
              Go to Settings →
            </button>
            <button onClick={() => setLicenseModal(null)}
              style={{ flex:1, backgroundColor:'#F3F4F6', color:'#374151', border:'none', padding:'12px', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'14px' }}>
              Dismiss
            </button>
          </div>
        </div>
      </div>
    )}
      </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5F7FA',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: '#1A3A2A',
    padding: '0',
    boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
    flexShrink: 0,
    zIndex: 100,
  },
  headerContent: {
    padding: '0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '56px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  hamburger: {
    background: 'none',
    border: 'none',
    color: '#FFFFFF',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  logo: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '800',
    letterSpacing: '-0.5px',
  },
  logoSafe: {
    color: '#FFFFFF',
  },
  logoCore: {
    color: '#D4AF37',
  },
  headerBadge: {
    backgroundColor: 'rgba(212, 175, 55, 0.15)',
    color: '#D4AF37',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  userName: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: '13px',
  },
  userRole: {
    color: '#D4AF37',
    fontSize: '11px',
    fontWeight: '500',
  },
  logoutBtn: {
    backgroundColor: 'transparent',
    color: '#FFFFFF',
    border: '1px solid rgba(255,255,255,0.2)',
    padding: '6px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '12px',
    transition: 'all 0.2s',
  },
  mainLayout: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  sidebar: {
    backgroundColor: '#FFFFFF',
    borderRight: '1px solid #E5E7EB',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    transition: 'width 0.2s ease',
  },
  mobileSwitch: {
    padding: '12px',
    borderBottom: '1px solid #E5E7EB',
  },
  mobileSwitchBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '10px',
    backgroundColor: '#EEF7F0',
    color: '#2D5C3F',
    border: '1px solid #C6E7CE',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '13px',
    transition: 'all 0.2s',
  },
  menuSection: {
    padding: '8px 0',
    borderBottom: '1px solid #F3F4F6',
  },
  sectionTitle: {
    padding: '8px 20px 4px',
    fontSize: '10px',
    fontWeight: '700',
    color: '#9CA3AF',
    letterSpacing: '1px',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 20px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    borderLeft: '3px solid transparent',
    color: '#4B5563',
    fontSize: '14px',
  },
  menuItemActive: {
    backgroundColor: '#EEF7F0',
    borderLeftColor: '#2D5C3F',
    color: '#1A3A2A',
    fontWeight: '600',
  },
  menuIcon: {
    fontSize: '18px',
    width: '24px',
    textAlign: 'center',
    flexShrink: 0,
  },
  menuLabel: {
    whiteSpace: 'nowrap',
  },
  companyInfo: {
    marginTop: 'auto',
    padding: '16px 20px',
    borderTop: '1px solid #E5E7EB',
  },
  companyName: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#374151',
  },
  companyPlan: {
    fontSize: '11px',
    color: '#9CA3AF',
    marginTop: '2px',
  },
  content: {
    flex: 1,
    padding: '24px',
    overflowY: 'auto',
    backgroundColor: '#F5F7FA',
  },
};

export default Layout;
